/**
 * Table pagination footer.
 *
 * Pairs with `usePaginationState()` from `@/lib/pagination`. Renders:
 *
 *   [ "10–25 of 1,234" ]   [ « ‹ page X of Y › » ]    [ 25 · 50 · 100 ]
 *
 * - Prev/next disabled at the boundaries
 * - Page-size chips swap data immediately (the hook resets page=1)
 * - Compact: lives directly under the bordered table block
 */
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { PAGE_SIZES, type PageSize } from "@/lib/pagination";

interface PaginationProps {
  page: number;
  pageSize: PageSize;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSize) => void;
  className?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  return (
    <div
      className={cn(
        // Softer border (60% opacity) to echo the table row dividers.
        "flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-3 py-2 text-xs text-muted-foreground",
        className,
      )}
    >
      <div className="tabular-nums">
        {total === 0 ? (
          t("pagination.empty", "No results")
        ) : (
          <>
            <span className="font-medium text-foreground">
              {start.toLocaleString()}–{end.toLocaleString()}
            </span>{" "}
            {t("pagination.of", "of")}{" "}
            <span className="font-medium text-foreground">{total.toLocaleString()}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          disabled={!canPrev}
          onClick={() => onPageChange(1)}
          aria-label={t("pagination.first", "First page")}
        >
          <ChevronsLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={!canPrev}
          onClick={() => onPageChange(safePage - 1)}
          aria-label={t("pagination.prev", "Previous page")}
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Button>
        <span className="px-2 tabular-nums">
          {t("pagination.page", "Page")}{" "}
          <span className="font-medium text-foreground">{safePage}</span>{" "}
          {t("pagination.ofShort", "/")} {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={!canNext}
          onClick={() => onPageChange(safePage + 1)}
          aria-label={t("pagination.next", "Next page")}
        >
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={!canNext}
          onClick={() => onPageChange(totalPages)}
          aria-label={t("pagination.last", "Last page")}
        >
          <ChevronsRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Button>
      </div>

      <div className="flex items-center gap-1">
        <span>{t("pagination.perPage", "Per page")}</span>
        <div role="group" className="inline-flex border border-border">
          {PAGE_SIZES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPageSizeChange(s)}
              className={cn(
                "px-2 py-1 font-mono text-xs transition-colors",
                s === pageSize
                  ? "bg-accent/15 text-foreground"
                  : "text-muted-foreground hover:bg-muted/40",
              )}
              aria-pressed={s === pageSize}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
